import type { ServicePrices } from '../api/types';
import { MAX_RATE_LIMIT, money, rateLimitInput } from '../utils/numberInput';

/** The four services, in the order both portals list them (ServicePricing.Services in Core). */
export const SERVICES: { key: keyof ServicePrices; label: string; help: string }[] = [
  { key: 'ageCheck', label: 'Age check', help: '/verify-age, and a KYC session in age mode' },
  { key: 'idExtract', label: 'ID extract', help: '/extract' },
  { key: 'idVerification', label: 'ID verification', help: '/verify' },
  { key: 'fullKyc', label: 'Full KYC', help: 'a KYC session, billed once when it completes' },
];

/**
 * Editor for a Custom client's terms: its request rate limit and its price per call for each
 * service. Used by Change Plan on the client page and by Add New Client. Controlled: the parent
 * owns the values. Same fields as Razor's _ServicePricesInputs; the server re-checks everything
 * (ClientPlanService.Validate).
 */
export function CustomTermsEditor({
  rateLimit,
  onRateLimit,
  prices,
  onPrices,
  idPrefix = 'custom',
}: {
  rateLimit: number;
  onRateLimit: (value: number) => void;
  prices: ServicePrices;
  onPrices: (value: ServicePrices) => void;
  idPrefix?: string;
}) {
  const error = validateTerms(rateLimit, prices);

  return (
    <>
      <div className="row g-3 mb-3">
        <div className="col-sm-6">
          <label className="form-label small fw-semibold" htmlFor={`${idPrefix}RateLimit`}>
            Rate limit
          </label>
          <div className="input-group input-group-sm">
            <input
              id={`${idPrefix}RateLimit`}
              type="number"
              className="form-control"
              min={1}
              max={MAX_RATE_LIMIT}
              step={1}
              required
              value={rateLimit}
              onChange={(e) => onRateLimit(Number(rateLimitInput(e.target.value)))}
            />
            <span className="input-group-text">req / min</span>
          </div>
        </div>
      </div>

      <ServicePricesInputs idPrefix={idPrefix} prices={prices} onPrices={onPrices} />
      {error && <div className="text-danger small mt-2">{error}</div>}
    </>
  );
}

/**
 * The four price-per-call inputs (also the Custom plan's defaults on Admin → Plans). Same fields as
 * Razor's _ServicePricesInputs. Money: at most 2 decimals, $0.01–$1,000.
 */
export function ServicePricesInputs({
  idPrefix,
  prices,
  onPrices,
}: {
  idPrefix: string;
  prices: ServicePrices;
  onPrices: (value: ServicePrices) => void;
}) {
  return (
    <>
      <label className="form-label small fw-semibold mb-1">Price per call</label>
      <div className="small text-body-secondary mb-2">
        The same at any volume. Test keys and Beta Live stay free.
      </div>
      <div className="row g-2">
        {SERVICES.map((s) => (
          <div className="col-sm-6" key={s.key}>
            <label className="form-label small mb-1" htmlFor={`${idPrefix}_${s.key}`}>
              {s.label}
            </label>
            <div className="input-group input-group-sm">
              <span className="input-group-text">$</span>
              <input
                id={`${idPrefix}_${s.key}`}
                type="number"
                className="form-control"
                min={0.01}
                max={1000}
                step={0.01}
                required
                value={prices[s.key]}
                onChange={(e) => onPrices({ ...prices, [s.key]: Number(money(e.target.value, 1000)) })}
              />
            </div>
            <div className="form-text mt-0">{s.help}</div>
          </div>
        ))}
      </div>
    </>
  );
}

/** Mirrors ServicePricing.Validate: $0.01–$1,000, whole cents. */
export function validatePrices(prices: ServicePrices): string | null {
  for (const s of SERVICES) {
    const price = prices[s.key];
    if (!(price >= 0.01 && price <= 1000)) return `${s.label}: the price must be between $0.01 and $1,000.`;
    if (Math.round(price * 100) / 100 !== price) return `${s.label}: the price can have at most 2 decimal places.`;
  }
  return null;
}

/** Mirrors ClientPlanService.Validate so the admin sees the problem before the round trip. */
export function validateTerms(rateLimit: number, prices: ServicePrices): string | null {
  if (!Number.isInteger(rateLimit) || rateLimit < 1 || rateLimit > MAX_RATE_LIMIT)
    return 'Rate limit must be a whole number between 1 and 10,000 requests per minute.';
  return validatePrices(prices);
}
